/**
 * Principal adapter + snapshot readers (S0.4) — the thin, framework-free read
 * path over a STAMPED profile. Zero-I/O, no React, no MSW — so the route guard
 * (`ProtectedRoute`, S1.2) and the list/detail handlers (S1.4) consume the exact
 * same predicates the resolver's own tests do.
 *
 * Two responsibilities:
 *   1. `fromProfile` — the single, memoized adapter from a stored profile
 *      (`GovernanceUser` or the auth `User`) to the resolver's `PrincipalIdentity`
 *      input, so the stored identity and the resolver input can never drift.
 *   2. `can` / `canInScope` — the thin readers over the snapshot the MSW layer
 *      stamps (`permissions[]` + `visibleOrgIds[]`). `can` is the Axis-A-only
 *      route-guard check; `canInScope` adds the Axis-B row check. They read the
 *      pre-stamped snapshot (NOT a live resolve) and are guaranteed to agree with
 *      `resolveEffectiveAccess` by the S0.4 shared-selector parity test.
 *
 * The NPE fix (plan §Slice 1.2, top invariant): `can` is null-safe on
 * `permissions` — the latent `profile?.permissions.includes(x)` crash (permissions
 * can be undefined, no `?? []` today) is centralized away here, so BOTH the
 * flag-ON path (`can`) and the guarded flag-OFF expression are crash-free.
 */

import type { PrincipalIdentity, RoleAssignment } from './types';
import { isRecordVisible } from './orgScope';

/**
 * The minimal stored-profile shape `fromProfile` reads — declared structurally so
 * BOTH `GovernanceUser` (`roleIds` + `assignments` + `homeOrgId`) and the auth
 * `User` (singular `role`, no scope) are assignable verbatim. A principal's roles
 * seed from `roleIds` when present, else the singular `role`.
 */
export interface ProfileLike {
  id: string;
  /** Multi-role identity (`GovernanceUser`). */
  roleIds?: string[];
  /** Single-role identity (auth `User`) — used only when `roleIds` is absent. */
  role?: string;
  homeOrgId?: string;
  assignments?: RoleAssignment[];
}

/**
 * The stamped snapshot `can`/`canInScope` read — the effective access the MSW
 * layer projected onto the profile at `/auth/me`. Both fields optional so a
 * pre-stamp / degenerate profile is safe (and so `User`/`GovernanceUser` are
 * assignable). `visibleOrgIds` absent ⇒ no Axis-B scope stamped.
 */
export interface PermissionSnapshot {
  permissions?: string[];
  visibleOrgIds?: string[];
}

/**
 * Identity-cache for `fromProfile` — the sanctioned memo the contract asks for
 * ("memoized adapter, single read path"). Referentially transparent: keyed by the
 * profile OBJECT identity, and profiles are treated as immutable snapshots (the
 * MSW layer clones on every write), so the same object always maps to the same
 * identity. Not shared mutable state that can change a result.
 */
const identityCache = new WeakMap<object, PrincipalIdentity>();

/**
 * Adapt a stored profile to the resolver's `PrincipalIdentity`. Memoized by object
 * identity. A nullish profile yields the degenerate empty identity (no roles, no
 * scope) so the single read path never throws.
 */
export function fromProfile(profile: ProfileLike | null | undefined): PrincipalIdentity {
  if (!profile) {
    return { userId: '', roleIds: [], assignments: [] };
  }
  const cached = identityCache.get(profile);
  if (cached) return cached;

  const roleIds =
    profile.roleIds && profile.roleIds.length > 0
      ? [...profile.roleIds]
      : profile.role
        ? [profile.role]
        : [];

  const identity: PrincipalIdentity = {
    userId: profile.id,
    roleIds,
    homeOrgId: profile.homeOrgId,
    assignments: profile.assignments ? [...profile.assignments] : [],
  };
  identityCache.set(profile, identity);
  return identity;
}

/**
 * Axis-A-only check over the stamped snapshot — the route-guard predicate
 * (`ProtectedRoute`, S1.2). Null-safe on `permissions` (the centralized NPE fix):
 * a nullish snapshot or absent `permissions` is simply "no grant". No admin
 * special-case — under rbacV2 the admin ROLE's `[...ALL_PERMISSIONS]` grant is
 * already stamped into `permissions`, so this stays a pure membership test.
 */
export function can(
  snapshot: PermissionSnapshot | null | undefined,
  permission: string
): boolean {
  const permissions = snapshot?.permissions;
  return Array.isArray(permissions) && permissions.includes(permission);
}

/**
 * Axis-A ∧ Axis-B check over the stamped snapshot — the list/detail predicate
 * (S1.4). Passes iff the principal holds `permission` AND the row's `orgId` is in
 * scope; shared master data (`orgId == null`) is always in scope (`isRecordVisible`).
 *
 * Note (degenerate-path guard, plan §Slice 0.3): an empty `visibleOrgIds` scopes
 * OUT every org-stamped row — correct for a principal with real assignments but a
 * lockout for a flag-OFF / no-assignment principal. The flag gate lives at the
 * CALL SITE (S1.4 only filters under `governance.rbacV2` AND for principals with
 * assignments); this predicate stays a pure, non-broadening Axis-A ∧ Axis-B test.
 */
export function canInScope(
  snapshot: PermissionSnapshot | null | undefined,
  permission: string,
  orgId: string | null | undefined
): boolean {
  if (!can(snapshot, permission)) return false;
  return isRecordVisible(orgId, new Set(snapshot?.visibleOrgIds ?? []));
}
